import React from 'react';
import { getBrandConfig } from '../utils/brandUtils';

export const PrivacyPolicy: React.FC = () => {
  const brandConfig = getBrandConfig();
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-base-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary mb-4">Privacy Policy</h1>
          <p className="text-lg text-base-content/70">
            {brandConfig.name} - Effective Date: {currentDate}
          </p>
        </div>

        {/* Content */}
        <div className="max-w-none">
          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Overview</h2>
            <p className="mb-4">
              This Privacy Policy explains how {brandConfig.legalEntity} ("we," "our," or "us") collects, uses, and protects information through {brandConfig.name}.
            </p>
            <p className="mb-4">
              We respect your privacy and are committed to handling Protected Health Information (PHI) and other personal data in compliance with HIPAA and applicable laws.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            <p className="mb-4">We may collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Contact Information (e.g., name, email).</li>
              <li>Account Information (e.g., login credentials).</li>
              <li>Billing Information (processed securely by Stripe).</li>
              <li>User Data/PHI (SOAP notes, PDFs, images uploaded by users).</li>
              <li>Usage Data (log files, device/browser type, IP address).</li>
            </ul>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Data</h2>
            <p className="mb-4">We use data to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide and improve the Service.</li>
              <li>Generate SOAP notes and documents.</li>
              <li>Process billing and account management.</li>
              <li>Ensure security and compliance.</li>
            </ul>
            <p className="mb-4">
              <strong>We do not sell or rent your data.</strong>
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Data Retention</h2>
            <p className="mb-4">
              SOAP notes and related PHI are stored for up to 30 days, unless deleted earlier by the user.
            </p>
            <p className="mb-4">
              Upon deletion or account closure, data is removed subject to limited backup retention.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Data Storage & Security</h2>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Data is stored in AWS data centers in the United States.</li>
              <li>All PHI is encrypted in transit (TLS) and at rest (AES-256).</li>
              <li>Access is restricted based on roles, with audit logging.</li>
              <li>Employees handling PHI undergo annual HIPAA training and background checks.</li>
            </ul>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Third-Party Vendors</h2>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>AWS provides hosting and infrastructure (covered by a signed BAA).</li>
              <li>Stripe processes payments.</li>
              <li>Google provides email services.</li>
            </ul>
            <p className="mb-4">
              All vendors handling PHI must be HIPAA-compliant and, where applicable, sign BAAs.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Patient Consent</h2>
            <p className="mb-4">
              Users are responsible for obtaining patient consent before uploading PHI into the Service.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. International Users</h2>
            <p className="mb-4">
              The Service is designed for U.S.-based healthcare providers. Users outside the U.S. are responsible for compliance with local data protection laws.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Children's Privacy</h2>
            <p className="mb-4">
              The Service is not intended for children under 13 (or 16 where applicable). We do not knowingly collect children's data.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Breach Notification</h2>
            <p className="mb-4">
              In the event of a data breach involving PHI, we will notify affected users and authorities as required by HIPAA.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy periodically. Changes will be communicated via email and posted with a new "Effective Date."
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
            <p className="mb-4">For privacy inquiries, contact us at:</p>
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <a 
                href={`mailto:${brandConfig.privacyEmail}`}
                className="text-primary hover:text-primary-focus font-semibold text-lg"
              >
                {brandConfig.privacyEmail}
              </a>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button 
            onClick={() => window.history.back()} 
            className="btn btn-primary"
          >
            ← Go Back
          </button>
        </div>
      </div>
    </div>
  );
};
